import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useFechavelComVoltar } from './VoltarDoCelular';
import './Confirmacao.css';

/**
 * A confirmação de "tem certeza?", com o desenho do produto.
 *
 * ── O que havia antes ─────────────────────────────────────────────────────
 *
 * Nove `window.confirm` — excluir transação, meta, investimento, método,
 * lembrete, categoria, dívida, e desvincular o WhatsApp. A caixa nativa não é
 * só feia: ela é do SISTEMA, não do app. Aparece colada no topo do navegador,
 * com a fonte do sistema, com "OK"/"Cancelar" em inglês em alguns aparelhos, e
 * dizendo o endereço do site em cima — no meio de uma tela que a pessoa acabou
 * de reconhecer como o app dela.
 *
 * E ela não sabe o que está apagando: `confirm()` só aceita texto corrido,
 * então o nome do que vai sumir vira parte de uma frase em vez de destaque.
 *
 * ── Por que promessa, e não um estado por chamador ───────────────────────
 *
 * Nove lugares teriam nove `useState` de "modal aberto" mais nove blocos de
 * JSX. `confirmar()` devolve uma promessa e o chamador continua sendo UMA
 * linha, igual ao `window.confirm` que ele substitui:
 *
 *   if (!await confirmar({ ... })) return;
 *
 * É o que faz a troca ser mecânica em vez de uma reescrita de cada tela.
 */

export interface PedidoDeConfirmacao {
  /**
   * Uma figura acima do título — hoje, o rosto do Finn.
   *
   * Existe para a caixa que não é de apagar: entrar no Finn Coach é combinar
   * algo COM alguém, e a caixa mostra com quem. Nas confirmações destrutivas
   * fica de fora de propósito: ali qualquer enfeite é ruído no momento em que
   * a pessoa decide apagar algo.
   */
  ilustracao?: React.ReactNode;
  titulo: string;
  /** O que vai acontecer, em uma frase. */
  texto?: React.ReactNode;
  /** O nome do que está sendo afetado. Vai em destaque, fora da frase. */
  alvo?: string;
  /** O rótulo do botão que CONFIRMA. "Excluir", "Desvincular" — nunca "OK". */
  acao?: string;
  /** Pinta o botão de confirmar como destrutivo. */
  perigo?: boolean;
  /**
   * O rótulo do botão que NÃO faz nada. "Cancelar" por padrão; muda quando a
   * ação é ela mesma um cancelamento ("Cancelar" ao lado de "Cancelar
   * assinatura" deixa a pessoa sem saber qual não faz nada — 22/10).
   */
  desistir?: string;
  /**
   * Uma escolha a mais dentro da caixa, quando confirmar não é uma coisa só.
   *
   * ── Por que CONTROLADA pelo chamador ─────────────────────────────────
   *
   * A alternativa era `confirmar` devolver `{ ok, marcado }`. Isso mexeria nos
   * onze chamadores para servir a um, e faria toda confirmação simples virar
   * `(await confirmar(...)).ok` — pior em dez lugares para melhorar um.
   *
   * O estado pertence mesmo a quem chamou: é ele que vai AGIR sobre a escolha
   * depois do `await`. A caixa só desenha e devolve o clique.
   *
   * Ela existe para o caso em que a pergunta tem duas partes — "excluir a
   * meta" e "e os aportes que você já lançou?" — e separar em duas caixas
   * seguidas seria pior que juntar numa.
   *
   * ── Por que o estado é da CAIXA e o valor volta por `aoResponder` ────
   *
   * A primeira versão era controlada pelo chamador (`marcado` + `aoMudar`), e
   * não funcionava: o provedor guarda o `pedido` em estado e renderiza a caixa
   * com aquele OBJETO. Mudar o estado lá fora re-renderiza o chamador, não o
   * objeto guardado — a caixa ficava congelada no valor inicial, e o chamador
   * ainda leria um closure velho depois do `await`.
   *
   * Agora a caixa é dona do que desenha, e o valor sai por uma chamada. Quem
   * pergunta guarda numa variável local, sem estado nenhum.
   */
  caixaDeSelecao?: {
    rotulo: React.ReactNode;
    /** A consequência do que está marcado, em uma frase. */
    nota?: React.ReactNode;
    /** Começa marcada? O padrão é não — ver a nota acima. */
    inicial?: boolean;
    /** Recebe o valor final quando a caixa é respondida. */
    aoResponder: (marcado: boolean) => void;
  };
}

type Confirmar = (p: PedidoDeConfirmacao) => Promise<boolean>;

const Ctx = createContext<Confirmar | null>(null);

/** `confirmar({...})` — devolve `true` se ela confirmou. */
export const useConfirmar = (): Confirmar => {
  const c = useContext(Ctx);
  if (!c) throw new Error('useConfirmar precisa do <ProvedorDeConfirmacao>.');
  return c;
};

const Caixa: React.FC<{
  pedido: PedidoDeConfirmacao;
  onResponder: (ok: boolean) => void;
}> = ({ pedido, onResponder }) => {
  const [marcado, setMarcado] = React.useState(pedido.caixaDeSelecao?.inicial ?? false);

  /*
   * O valor da caixa sai junto com a resposta, e só quando ela CONFIRMA.
   *
   * Avisar no cancelamento faria o chamador guardar uma escolha de uma ação
   * que não aconteceu — e a próxima exclusão herdaria a marcação de uma meta
   * diferente.
   */
  const responder = React.useCallback((ok: boolean) => {
    if (ok) pedido.caixaDeSelecao?.aoResponder(marcado);
    onResponder(ok);
  }, [onResponder, pedido, marcado]);

  const cancelar = React.useCallback(() => responder(false), [responder]);
  useTravarScroll();
  const caixa = useRef<HTMLDivElement>(null);
  useFocoNoModal(caixa);
  /* O voltar do celular CANCELA, igual ao Esc e ao toque fora. */
  useFechavelComVoltar(true, cancelar);

  /*
   * `Esc` cancela. É o que a caixa nativa fazia, e tirar um caminho de saída
   * que já existia seria uma piora disfarçada de melhoria.
   */
  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') responder(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [responder]);

  return createPortal(
    <div className="cfm-veu" onClick={() => responder(false)} ref={caixa}>
      <div
        className="cfm-caixa"
        onClick={e => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-label={pedido.titulo}
      >
        {/* O ✕ (22/10, pedido dela): a saída que todo modal do app tem. Faz o
            mesmo que o Cancelar, o Esc e o toque fora. Sendo o primeiro botão,
            leva o foco inicial — e continua sendo um que não apaga nada. */}
        <button type="button" className="cfm-fechar" onClick={() => responder(false)} aria-label="Fechar">✕</button>
        {pedido.ilustracao && <div className="cfm-ilustracao">{pedido.ilustracao}</div>}
        <h3 className="cfm-titulo">{pedido.titulo}</h3>
        {pedido.alvo && <p className="cfm-alvo">{pedido.alvo}</p>}
        {/* `div`, não `p` (22/10): o texto pode trazer um controle, como o
            horário dos avisos, e `div` dentro de `p` é HTML inválido. */}
        {pedido.texto && <div className="cfm-texto">{pedido.texto}</div>}

        {pedido.caixaDeSelecao && (
          <label className="cfm-selecao">
            <input
              type="checkbox"
              checked={marcado}
              onChange={e => setMarcado(e.target.checked)}
            />
            <span>
              {pedido.caixaDeSelecao.rotulo}
              {/* A consequência vem junto e não escondida: uma opção que mexe
                  em dinheiro precisa dizer no que ela mexe. */}
              {pedido.caixaDeSelecao.nota && (
                <em className="cfm-selecao-nota">{pedido.caixaDeSelecao.nota}</em>
              )}
            </span>
          </label>
        )}

        <div className="cfm-acoes">
          {/*
            Cancelar vem PRIMEIRO na ordem do DOM, e por isso recebe o foco
            inicial. Numa caixa cuja ação é destrutiva, o botão sob o dedo (e
            sob o Enter) tem que ser o que não apaga nada.
          */}
          <button className="cfm-btn cfm-cancelar" onClick={() => responder(false)}>
            {pedido.desistir ?? 'Cancelar'}
          </button>
          <button
            className={`cfm-btn cfm-confirmar${pedido.perigo ? ' perigo' : ''}`}
            onClick={() => responder(true)}
          >
            {pedido.acao ?? 'Confirmar'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const ProvedorDeConfirmacao: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pedido, setPedido] = useState<PedidoDeConfirmacao | null>(null);
  const responder = useRef<(ok: boolean) => void>(() => undefined);

  const confirmar = useCallback<Confirmar>(p => new Promise<boolean>(resolve => {
    responder.current = resolve;
    setPedido(p);
  }), []);

  const fechar = useCallback((ok: boolean) => {
    setPedido(null);
    responder.current(ok);
  }, []);

  return (
    <Ctx.Provider value={confirmar}>
      {children}
      {pedido && <Caixa pedido={pedido} onResponder={fechar} />}
    </Ctx.Provider>
  );
};
